import { createFileRoute, Link, notFound } from "@tanstack/react-router";

import { breadcrumbJsonLd, canonicalUrl, seo, SITE_URL } from "@/lib/seo";
import { Section } from "@/components/Section";
import { Container } from "@/components/Container";
import { Eyebrow } from "@/components/Eyebrow";
import { Aurora } from "@/components/Aurora";
import { CtaButton } from "@/components/CtaButton";
import { PagePlaceholder } from "@/components/site/PagePlaceholder";
import { PostBody } from "@/components/blog/PostBody";
import { PostList, formatPostDate } from "@/components/blog/PostList";
import { CATEGORY_LABELS, getPost, getPosts, readingMinutes } from "@/content/posts";

const SERVICE_LABELS: Record<string, string> = {
  "/services/ai-opportunity-assessment": "AI opportunity assessment",
  "/services/ai-implementation": "AI implementation",
  "/services/workflow-automation": "AI workflow automation",
  "/services/ai-enablement": "AI training and adoption",
  "/tools/ai-opportunity-scorecard": "Free AI readiness scorecard",
  "/book": "Book a free AI call",
};

export const Route = createFileRoute("/_site/blog/$slug")({
  head: ({ params }) => {
    const post = getPost(params.slug);
    if (!post) {
      return seo({
        title: "Article",
        description: "An article from LAA Concierge Consulting.",
        path: `/blog/${params.slug}`,
        noindex: true,
      });
    }
    const url = canonicalUrl(`/blog/${post.slug}`);
    return seo({
      title: post.seoTitle,
      description: post.description,
      path: `/blog/${post.slug}`,
      type: "article",
      publishedTime: post.publishedAt,
      modifiedTime: post.updatedAt,
      jsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: post.title,
          description: post.description,
          datePublished: post.publishedAt,
          dateModified: post.updatedAt,
          articleSection: CATEGORY_LABELS[post.category],
          mainEntityOfPage: url,
          url,
          author: { "@id": `${SITE_URL}/#organization` },
          publisher: { "@id": `${SITE_URL}/#organization` },
        },
        breadcrumbJsonLd([
          { name: "Articles", path: "/blog" },
          { name: post.title, path: `/blog/${post.slug}` },
        ]),
      ],
    });
  },
  loader: ({ params }) => {
    const post = getPost(params.slug);
    if (!post) throw notFound();
    return { post };
  },
  component: Page,
  errorComponent: () => (
    <PagePlaceholder title="This article didn't load" intro="Try refreshing the page." />
  ),
  notFoundComponent: () => (
    <PagePlaceholder title="Article not found" intro="This article doesn't exist or was moved." />
  ),
});

function Page() {
  const params = Route.useParams();
  const post = getPost(params.slug)!;
  const serviceLabel = SERVICE_LABELS[post.primaryService] ?? "Our services";

  const others = getPosts().filter((p) => p.slug !== post.slug);
  const more = [
    ...others.filter((p) => p.category === post.category),
    ...others.filter((p) => p.category !== post.category),
  ].slice(0, 3);

  return (
    <>
      <Section tone="paper" eager className="relative overflow-hidden">
        <Aurora variant="light" className="opacity-70" />
        <Container variant="narrow" className="relative">
          <nav aria-label="Breadcrumb">
            <ol className="flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-eyebrow uppercase text-ink-500">
              <li>
                <Link
                  to="/"
                  className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-sm text-brand-700 transition-brand hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-700"
                >
                  Home
                </Link>
              </li>
              <li aria-hidden="true">/</li>
              <li>
                <Link
                  to="/blog"
                  className="inline-flex min-h-[44px] items-center rounded-sm text-brand-700 transition-brand hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-700"
                >
                  Articles
                </Link>
              </li>
              <li aria-hidden="true">/</li>
              <li aria-current="page" className="normal-case">
                {post.title}
              </li>
            </ol>
          </nav>

          <div className="mt-8">
            <Eyebrow>{CATEGORY_LABELS[post.category]}</Eyebrow>
            <h1
              className="mt-5 font-display text-display-lg text-ink-900"
              style={{ textWrap: "balance" }}
            >
              {post.title}
            </h1>
          </div>
          <p className="mt-6 font-mono text-eyebrow uppercase text-ink-500">
            {formatPostDate(post.publishedAt)} · {readingMinutes(post)} min read
          </p>
        </Container>
      </Section>

      <Section tone="raised">
        <Container variant="narrow">
          <PostBody blocks={post.body} />

          <div className="mt-16 rounded-2xl border border-ink-200 bg-paper-0 p-6 md:p-8">
            <h2 className="font-display text-h3 text-ink-900">Where this leads</h2>
            <ul className="mt-4 space-y-3 text-body-lg text-ink-600">
              <li>
                <Link
                  to={post.primaryService as never}
                  className="inline-flex min-h-[44px] items-center text-brand-700 underline underline-offset-4 hover:text-brand-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-700"
                >
                  {serviceLabel}
                </Link>
              </li>
              <li>
                <Link
                  to="/tools/ai-opportunity-scorecard"
                  className="inline-flex min-h-[44px] items-center text-brand-700 underline underline-offset-4 hover:text-brand-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-700"
                >
                  Free AI readiness scorecard
                </Link>
              </li>
            </ul>
            <div className="mt-6">
              <CtaButton asChild>
                <Link to="/book">Book a Free AI Call</Link>
              </CtaButton>
            </div>
          </div>
        </Container>
      </Section>

      {more.length > 0 && (
        <Section tone="paper">
          <Container>
            <h2 className="font-display text-h2 text-ink-900">More articles</h2>
            <div className="mt-8">
              <PostList posts={more} />
            </div>
          </Container>
        </Section>
      )}
    </>
  );
}
